import { useRef, useState } from 'react';
import { apiUrl } from '../../shared/api/apiConfig';
import './WeighingSubmitPage.css';

const SEEDED_SCANNER_DEVICE_ID = 'SCAN-01';
const SEEDED_SCANNER_DEVICE_KEY = 'SCAN-01-DEV-KEY';
const SEEDED_SCALE_DEVICE_ID = 'SCALE-01';
const SEEDED_SCALE_DEVICE_KEY = 'SCALE-01-DEV-KEY';
const SEEDED_PALLET_CODE = 'T-000001';

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function StepItem({ stepNum, method, endpoint, statusText, ok, body }) {
  return (
    <div className="ws-step-item">
      <div className="ws-step-header">
        <span className={`ws-status-dot ${ok ? 'ok' : 'warn'}`} />
        <span className="ws-step-num">STEP {stepNum}</span>
        <span className={`ws-step-method ${method.toLowerCase()}`}>{method}</span>
        <span className="ws-step-endpoint">{endpoint}</span>
        <span className={`ws-step-status ${ok ? 'ok' : 'err'}`}>{statusText}</span>
      </div>
      <div className="ws-step-body">
        {/* eslint-disable-next-line react/no-danger */}
        <pre dangerouslySetInnerHTML={{ __html: escapeHtml(body) }} />
      </div>
    </div>
  );
}

function DeviceCard({ title, subtitle, defaultDeviceId, defaultDeviceKey, defaultReadingValue, onResult }) {
  const [deviceId, setDeviceId] = useState(defaultDeviceId);
  const [deviceKey, setDeviceKey] = useState(defaultDeviceKey);
  const [readingValue, setReadingValue] = useState(defaultReadingValue);
  const [loading, setLoading] = useState(false);
  const [spinnerMsg, setSpinnerMsg] = useState('Connecting...');
  const [steps, setSteps] = useState([]);
  const [successVisible, setSuccessVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const abortRef = useRef(null);

  async function handleSubmit() {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    setLoading(true);
    setSteps([]);
    setSuccessVisible(false);
    setErrorMsg(null);

    try {
      // Step 1 — resolve
      setSpinnerMsg('Step 1 — resolving weighing request...');
      const getUrl = apiUrl(`/WeighingRequests/resolve?deviceId=${encodeURIComponent(deviceId)}`);
      const getRes = await fetch(getUrl, {
        method: 'GET',
        headers: { 'X-Device-Key': deviceKey },
        signal,
      });
      const getText = await getRes.text();
      let getPretty = getText;
      try { getPretty = JSON.stringify(JSON.parse(getText), null, 2); } catch { /* raw text */ }

      setSteps((prev) => [
        ...prev,
        { stepNum: 1, method: 'GET', endpoint: '/api/WeighingRequests/resolve',
          statusText: `${getRes.status} ${getRes.statusText}`, ok: getRes.ok, body: getPretty },
      ]);

      if (!getRes.ok) throw new Error(`Resolve failed: ${getRes.status} ${getRes.statusText}`);

      let requestId;
      try {
        const data = JSON.parse(getText);
        requestId = data.requestId ?? data.RequestId ?? data.id ?? data.Id;
      } catch { /* ignore */ }
      if (!requestId) throw new Error('Could not extract requestId from resolve response.');

      // Step 2 — submit
      setSpinnerMsg('Step 2 — submitting reading...');
      const payload = {
        requestId,
        deviceId,
        readingValue,
        occurredAtUtc: new Date().toISOString(),
      };
      const postRes = await fetch(apiUrl('/RequestReadings/submit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Device-Key': deviceKey },
        body: JSON.stringify(payload),
        signal,
      });
      const postText = await postRes.text();
      let postPretty = postText || '(empty body)';
      try { postPretty = JSON.stringify(JSON.parse(postText), null, 2); } catch { /* raw text */ }

      setSteps((prev) => [
        ...prev,
        { stepNum: 2, method: 'POST', endpoint: '/api/RequestReadings/submit',
          statusText: `${postRes.status} ${postRes.statusText}`, ok: postRes.ok, body: postPretty },
      ]);

      if (!postRes.ok) throw new Error(`Submit failed: ${postRes.status} ${postRes.statusText}`);

      setSuccessVisible(true);
      onResult(true);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setErrorMsg(err.message);
      onResult(false);
    } finally {
      setLoading(false);
    }
  }

  const hasLog = steps.length > 0 || successVisible || errorMsg;

  return (
    <div className="ws-card">
      <div className="ws-card-header">
        <div className="ws-card-icon">⚖️</div>
        <div>
          <div className="ws-card-title">{title}</div>
          <div className="ws-card-subtitle">{subtitle}</div>
        </div>
      </div>

      <div className="ws-flow-strip">
        <div className="ws-flow-node">
          <span className="ws-flow-pill get">GET</span>
          <span className="ws-flow-node-label">WeighingRequests/resolve</span>
        </div>
        <div className="ws-flow-sep">›</div>
        <div className="ws-flow-node">
          <span className="ws-flow-pill mid">requestId</span>
          <span className="ws-flow-node-label">auto-extracted</span>
        </div>
        <div className="ws-flow-sep">›</div>
        <div className="ws-flow-node">
          <span className="ws-flow-pill post">POST</span>
          <span className="ws-flow-node-label">RequestReadings/submit</span>
        </div>
      </div>

      <div className="ws-fields-wrap">
        <div className="ws-field-group ws-full">
          <label className="ws-field-label">
            Device ID <span className="ws-field-tag ws-query">query param</span>
          </label>
          <input
            type="text"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="e.g. SCAN-01 or SCALE-01"
          />
        </div>
        <div className="ws-field-group ws-full">
          <label className="ws-field-label">
            X-Device-Key <span className="ws-field-tag ws-header">header</span>
          </label>
          <input
            type="text"
            value={deviceKey}
            onChange={(e) => setDeviceKey(e.target.value)}
            placeholder="e.g. SCAN-01-DEV-KEY"
          />
        </div>
        <div className="ws-field-group">
          <label className="ws-field-label">
            Reading Value <span className="ws-field-tag ws-body">body</span>
          </label>
          <input
            type="text"
            value={readingValue}
            onChange={(e) => setReadingValue(e.target.value)}
            placeholder="e.g. 70.50"
          />
        </div>
      </div>

      <div className="ws-btn-wrap">
        <button
          type="button"
          className="ws-submit-btn"
          disabled={loading}
          onClick={handleSubmit}
        >
          <span className="ws-btn-icon">⚡</span>
          {title}
        </button>
      </div>

      <div className="ws-log-wrap">
        {!hasLog && !loading && (
          <div className="ws-log-empty">
            <div className="ws-log-empty-icon">📡</div>
            Press the button to submit a reading
          </div>
        )}

        {loading && (
          <div className="ws-spinner-row">
            <div className="ws-spinner" />
            <span>{spinnerMsg}</span>
          </div>
        )}

        {steps.map((s) => (
          <StepItem key={s.stepNum} {...s} />
        ))}

        {successVisible && (
          <div className="ws-success-banner">
            <span>✅</span> Reading submitted successfully
          </div>
        )}

        {errorMsg && (
          <div className="ws-error-banner">
            <span>⚠️</span> <span>{errorMsg}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function WeighingSubmitPage() {
  const [okCount, setOkCount] = useState(0);
  const [failCount, setFailCount] = useState(0);

  function handleResult(success) {
    if (success) setOkCount((n) => n + 1);
    else setFailCount((n) => n + 1);
  }

  return (
    <div className="ws-page">
      <div className="ws-page-header">
        <div className="ws-header-text">
          <div className="ws-header-breadcrumb">Device Testing</div>
          <div className="ws-header-title">Weighing Request Submission</div>
          <div className="ws-header-desc">
            Submit weight readings from your scanner and scale devices. The system will
            automatically resolve the active weighing request and record the measurements.
          </div>
        </div>
        <div className="ws-header-stats">
          <div className="ws-stat-box">
            <div className="ws-stat-num">{okCount}</div>
            <div className="ws-stat-label">Submitted</div>
          </div>
          <div className="ws-stat-box">
            <div className="ws-stat-num">{failCount}</div>
            <div className="ws-stat-label">Failed</div>
          </div>
        </div>
      </div>

      <div className="ws-cards-container">
        <DeviceCard
          title="กล้องอ่านคิวอาร์โค๊ด พาเลท"
          subtitle="Resolve active request → submit pallet code"
          defaultDeviceId={SEEDED_SCANNER_DEVICE_ID}
          defaultDeviceKey={SEEDED_SCANNER_DEVICE_KEY}
          defaultReadingValue={SEEDED_PALLET_CODE}
          onResult={handleResult}
        />
        <DeviceCard
          title="เครื่องชั่งอ่านน้ำหนัก"
          subtitle="Resolve active request → submit weight reading"
          defaultDeviceId={SEEDED_SCALE_DEVICE_ID}
          defaultDeviceKey={SEEDED_SCALE_DEVICE_KEY}
          defaultReadingValue="70.50"
          onResult={handleResult}
        />
      </div>
    </div>
  );
}
